import Konva from 'konva';
import { escapeHtml } from '../utils/html.js';

const PITCH_RATIO = 105 / 68;
const TEAM_COLORS = {
  A: { main: '#2f80ed', soft: 'rgba(47,128,237,0.18)', line: 'rgba(151,202,255,0.9)' },
  B: { main: '#eb5757', soft: 'rgba(235,87,87,0.18)', line: 'rgba(255,176,176,0.9)' }
};
const MIN_COACH_PLAYERS = 10;

export function computeStoryboardStageSize(width) {
  const w = Math.max(280, Math.round(Number(width) || 360));
  return { width: w, height: Math.round(w / PITCH_RATIO) };
}

export function renderStoryboardReplay(container, storyboard, videoEl, context = {}) {
  const steps = buildStoryboardSteps(storyboard, context);
  if (!container || steps.length === 0) return;

  const wrapper = document.createElement('div');
  wrapper.className = 'glass-card storyboard-replay';
  wrapper.innerHTML = `
    <div class="section-head">
      <div>
        <p class="eyebrow">Reconstitution interactive</p>
        <h2 class="heading-2">Tableau tactique animé</h2>
      </div>
      <button id="story-play" class="btn-primary" type="button">Lecture</button>
    </div>
    <div class="storyboard-layout">
      <div>
        <div id="story-stage" class="story-stage"></div>
        <div class="story-controls">
          <button id="story-prev" class="btn-secondary" type="button">Précédent</button>
          <span id="story-counter" class="text-muted"></span>
          <button id="story-next" class="btn-secondary" type="button">Suivant</button>
        </div>
      </div>
      <div class="story-panel">
        <p id="story-intensity" class="story-badge"></p>
        <h3 id="story-title"></h3>
        <p id="story-narration"></p>
        <div id="story-metrics" class="story-metrics"></div>
        <div id="story-annotations" class="story-annotations"></div>
        <div class="story-timeline">
          ${steps.map((step, index) => `
            <button class="story-dot" type="button" data-index="${index}" aria-label="Étape ${index + 1}">
              <span>${index + 1}</span>
            </button>
          `).join('')}
        </div>
      </div>
    </div>
  `;
  container.appendChild(wrapper);

  const stageContainer = wrapper.querySelector('#story-stage');
  const { width, height } = computeStoryboardStageSize(stageContainer.clientWidth || 360);
  stageContainer.style.height = `${height}px`;

  const stage = new Konva.Stage({ container: stageContainer, width, height });
  const layer = new Konva.Layer();
  stage.add(layer);

  let current = 0;
  let timer = null;

  const draw = (index) => {
    current = Math.max(0, Math.min(index, steps.length - 1));
    const step = steps[current];
    const previousStep = current > 0 ? steps[current - 1] : null;
    layer.destroyChildren();
    drawPitch(layer, width, height);
    drawStep(layer, width, height, step, previousStep);
    layer.draw();

    wrapper.querySelector('#story-counter').textContent = `${current + 1}/${steps.length}`;
    wrapper.querySelector('#story-title').textContent = step.title || `Étape ${current + 1}`;
    wrapper.querySelector('#story-narration').textContent = step.narration || '';
    wrapper.querySelector('#story-intensity').textContent = `Intensité ${step.intensity || 'medium'} · ${step.focus || 'zone'}`;
    wrapper.querySelector('#story-metrics').innerHTML = renderStepMetrics(step);
    wrapper.querySelector('#story-annotations').innerHTML = (step.annotations || [])
      .slice(0, 4)
      .map((item) => `<span>${escapeHtml(item.label || '')}</span>`)
      .join('');
    wrapper.querySelectorAll('.story-dot').forEach((dot, dotIndex) => {
      dot.classList.toggle('active', dotIndex === current);
    });

    if (videoEl && Number.isFinite(step.time_start)) {
      videoEl.currentTime = Math.max(0, Number(step.time_start));
    }
  };

  const stop = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    wrapper.querySelector('#story-play').textContent = 'Lecture';
  };

  const playFrom = (index) => {
    stop();
    draw(index);
    wrapper.querySelector('#story-play').textContent = 'Pause';
    const tick = () => {
      if (current >= steps.length - 1) {
        stop();
        return;
      }
      const step = steps[current];
      const duration = Math.max(900, Math.min(2400, ((step.time_end || step.time_start || 0) - (step.time_start || 0)) * 1000 || 1400));
      timer = setTimeout(() => {
        draw(current + 1);
        tick();
      }, duration);
    };
    tick();
  };

  wrapper.querySelector('#story-prev').addEventListener('click', () => { stop(); draw(current - 1); });
  wrapper.querySelector('#story-next').addEventListener('click', () => { stop(); draw(current + 1); });
  wrapper.querySelector('#story-play').addEventListener('click', () => {
    if (timer) stop();
    else playFrom(current);
  });
  wrapper.querySelectorAll('.story-dot').forEach((dot) => {
    dot.addEventListener('click', () => { stop(); draw(Number(dot.dataset.index)); });
  });

  const ro = new ResizeObserver(() => {
    const { width: w, height: h } = computeStoryboardStageSize(stageContainer.clientWidth || width);
    stage.width(w);
    stage.height(h);
    stage.scale({ x: w / width, y: h / height });
    stage.draw();
  });
  ro.observe(stageContainer);

  draw(0);
}

export function buildStoryboardSteps(storyboard, context = {}) {
  const rawSteps = Array.isArray(storyboard) ? storyboard.filter(Boolean) : [];
  const contextPlayers = normalizeViewPlayers(context.players_positions);
  const contextBall = normalizeBall(context.ball_position);
  const contextArrows = normalizeViewArrows(context.arrows);
  const contextZones = normalizeViewZones(context.highlight_zones);

  return rawSteps.map((step, index) => {
    const previous = index > 0 ? rawSteps[index - 1] : null;
    const next = index < rawSteps.length - 1 ? rawSteps[index + 1] : null;
    const ball = normalizeBall(step.ball_position) || contextBall || inferBallFromArrows(step.arrows) || inferBallFromPlayers(step.players_positions);
    const players = completeCoachPlayers(
      normalizeViewPlayers(step.players_positions),
      normalizeViewPlayers(previous?.players_positions),
      normalizeViewPlayers(next?.players_positions),
      contextPlayers,
      ball,
      index,
      rawSteps.length
    );
    const arrows = normalizeViewArrows(step.arrows);
    const effectiveArrows = arrows.length ? arrows : inferCoachArrows(players, ball, index, rawSteps.length, contextArrows);
    const annotations = enrichAnnotations(step.annotations, contextZones, players, ball);
    return {
      ...step,
      players_positions: players,
      ball_position: ball || {},
      arrows: effectiveArrows,
      annotations,
      _derivedPlayers: players.filter((p) => p.derived).length,
      _teamCount: Object.keys(groupByTeam(players)).length
    };
  });
}

function drawStep(layer, width, height, step, previousStep = null) {
  const players = normalizeViewPlayers(step.players_positions);
  const previousPlayers = normalizeViewPlayers(previousStep?.players_positions);

  drawLaneGuides(layer, width, height);
  drawActiveZone(layer, width, height, step, players);
  drawTeamBlocks(layer, width, height, players);
  drawBlockEvolution(layer, width, height, players, previousPlayers);
  drawTeamLines(layer, width, height, players);
  drawSupportNetwork(layer, width, height, players, step.ball_position);
  drawBallTrail(layer, width, height, previousStep?.ball_position, step.ball_position);

  players.forEach((player) => {
    const x = pct(player.x, width);
    const y = pct(player.y, height);
    const color = teamColor(player.team).main;
    const derived = player.derived === true;
    layer.add(new Konva.Circle({
      x,
      y,
      radius: Math.max(7, width * 0.018),
      fill: derived ? '#ffffff' : color,
      stroke: derived ? color : '#fff',
      strokeWidth: derived ? 2.5 : 2,
      opacity: derived ? 0.86 : 1
    }));
    layer.add(new Konva.Text({
      x: x - 12,
      y: y - 7,
      text: roleLabel(player.role),
      fontSize: 10,
      fontStyle: 'bold',
      fill: derived ? color : '#fff',
      width: 24,
      align: 'center'
    }));
    if (Number.isFinite(player.movement_x) && Number.isFinite(player.movement_y)) {
      layer.add(new Konva.Arrow({
        points: [x, y, pct(player.movement_x, width), pct(player.movement_y, height)],
        pointerLength: 8,
        pointerWidth: 6,
        stroke: color,
        fill: color,
        strokeWidth: 2,
        dash: [5, 5],
        opacity: 0.8
      }));
    }
  });

  (step.arrows || []).forEach((arrow) => {
    const typeColor = arrow.type === 'shot' ? '#f2994a' : arrow.type === 'press' ? '#eb5757' : arrow.type === 'cover' ? '#9b51e0' : '#f2c94c';
    layer.add(new Konva.Arrow({
      points: [pct(arrow.from_x, width), pct(arrow.from_y, height), pct(arrow.to_x, width), pct(arrow.to_y, height)],
      pointerLength: 10,
      pointerWidth: 7,
      stroke: typeColor,
      fill: typeColor,
      strokeWidth: arrow.type === 'shot' ? 4 : 3,
      dash: arrow.type === 'run' || arrow.type === 'press' ? [7, 5] : []
    }));
    if (arrow.label) {
      layer.add(new Konva.Label({ x: pct(arrow.to_x, width) + 4, y: pct(arrow.to_y, height) - 16 })
        .add(new Konva.Tag({ fill: '#ffffff', opacity: 0.88, cornerRadius: 4 }))
        .add(new Konva.Text({ text: arrow.label, fontSize: 11, fill: '#101815', padding: 4 })));
    }
  });

  (step.annotations || []).forEach((annotation) => {
    const x = pct(annotation.x, width);
    const y = pct(annotation.y, height);
    layer.add(new Konva.Ring({
      x,
      y,
      innerRadius: width * 0.05,
      outerRadius: width * 0.075,
      fill: '#c6ff4d',
      opacity: 0.28
    }));
  });

  const ball = step.ball_position;
  if (ball && Number.isFinite(ball.x) && Number.isFinite(ball.y)) {
    layer.add(new Konva.Circle({
      x: pct(ball.x, width),
      y: pct(ball.y, height),
      radius: width * 0.019,
      fill: '#f2c94c',
      stroke: '#fff',
      strokeWidth: 1
    }));
  }
}

function renderStepMetrics(step) {
  const players = normalizeViewPlayers(step.players_positions);
  const teams = groupByTeam(players);
  const bits = [
    `${players.length} joueurs`,
    `${Object.keys(teams).length} équipes`,
    `${(step.arrows || []).length} mouvements`
  ];
  if (step._derivedPlayers > 0) bits.push(`${step._derivedPlayers} repères pédagogiques`);
  return bits.map((bit) => `<span>${escapeHtml(bit)}</span>`).join('');
}

export function inferStoryboardTeamLines(players = []) {
  const byTeam = groupByTeam(normalizeViewPlayers(players));
  return Object.entries(byTeam).flatMap(([team, teamPlayers]) => inferLinesForTeam(team, teamPlayers));
}

function normalizeViewPlayers(players = []) {
  if (!Array.isArray(players)) return [];
  return players
    .map((p) => ({
      team: p && p.team === 'B' ? 'B' : 'A',
      role: typeof p?.role === 'string' ? p.role : '',
      x: Number(p?.x),
      y: Number(p?.y),
      movement_x: Number(p?.movement_x),
      movement_y: Number(p?.movement_y),
      derived: p?.derived === true
    }))
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
}

function normalizeBall(ball) {
  if (!ball || typeof ball !== 'object') return null;
  const x = Number(ball.x);
  const y = Number(ball.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x: clampPctValue(x), y: clampPctValue(y) };
}

function normalizeViewArrows(arrows = []) {
  if (!Array.isArray(arrows)) return [];
  return arrows
    .map((a) => ({
      type: ['pass', 'run', 'shot', 'press', 'cover'].includes(a?.type) ? a.type : 'pass',
      from_x: Number(a?.from_x),
      from_y: Number(a?.from_y),
      to_x: Number(a?.to_x),
      to_y: Number(a?.to_y),
      label: typeof a?.label === 'string' ? a.label.slice(0, 42) : ''
    }))
    .filter((a) => [a.from_x, a.from_y, a.to_x, a.to_y].every(Number.isFinite))
    .slice(0, 8);
}

function normalizeViewZones(zones = []) {
  if (!Array.isArray(zones)) return [];
  return zones
    .map((z) => ({
      x: Number(z?.x),
      y: Number(z?.y),
      label: typeof z?.label === 'string' ? z.label.slice(0, 56) : ''
    }))
    .filter((z) => Number.isFinite(z.x) && Number.isFinite(z.y))
    .slice(0, 3);
}

function completeCoachPlayers(current, previous, next, contextPlayers, ball, stepIndex, stepCount) {
  const base = dedupePlayers(current);
  const pools = [contextPlayers, previous, next].flat().filter(Boolean);
  pools.forEach((p) => {
    if (base.length >= MIN_COACH_PLAYERS) return;
    if (hasNearbyRole(base, p)) return;
    base.push({ ...p, derived: p.derived || current.length === 0 });
  });

  const withRoles = assignMissingRoles(base);
  if (withRoles.length >= MIN_COACH_PLAYERS && hasTwoTeams(withRoles)) return withRoles.slice(0, 14);

  return mergeCoachTemplate(withRoles, ball, stepIndex, stepCount).slice(0, 14);
}

function dedupePlayers(players) {
  const out = [];
  players.forEach((p) => {
    if (!hasNearbyRole(out, p)) out.push({ ...p });
  });
  return out;
}

function hasNearbyRole(players, p) {
  return players.some((q) => q.team === p.team && roleFamily(q.role) === roleFamily(p.role) && Math.hypot(q.x - p.x, q.y - p.y) < 8);
}

function hasTwoTeams(players) {
  const teams = new Set(players.map((p) => p.team));
  return teams.has('A') && teams.has('B');
}

function assignMissingRoles(players) {
  const byTeam = groupByTeam(players);
  Object.values(byTeam).forEach((teamPlayers) => {
    const sorted = teamPlayers.slice().sort((a, b) => a.x - b.x);
    sorted.forEach((p, index) => {
      if (p.role) return;
      const ratio = sorted.length <= 1 ? 0.5 : index / (sorted.length - 1);
      p.role = ratio < 0.34 ? 'défenseur' : ratio < 0.7 ? 'milieu' : 'attaquant';
    });
  });
  return players;
}

function mergeCoachTemplate(players, ball, stepIndex, stepCount) {
  const out = [...players];
  const template = coachTemplate(ball, stepIndex, stepCount);
  template.forEach((p) => {
    if (out.length >= MIN_COACH_PLAYERS && hasTwoTeams(out)) return;
    if (hasNearbyRole(out, p)) return;
    out.push(p);
  });
  return out;
}

function coachTemplate(ball, stepIndex, stepCount) {
  const bx = Number.isFinite(ball?.x) ? ball.x : 48 + (stepIndex - Math.max(0, stepCount - 1) / 2) * 8;
  const by = Number.isFinite(ball?.y) ? ball.y : 50;
  const phase = stepCount > 1 ? stepIndex / (stepCount - 1) : 0.5;
  const push = (phase - 0.5) * 10;
  const attackX = clampPctValue(bx - 16 + push);
  const defendX = clampPctValue(bx + 14 + push * 0.35);
  const lanes = [18, 34, 50, 66, 82];
  return [
    ...lanes.map((y, i) => ({
      team: 'A',
      role: i === 0 || i === 4 ? 'ailier' : i === 2 ? 'milieu' : 'attaquant',
      x: clampPctValue(attackX + (i === 2 ? 14 : i % 2 ? 4 : -4)),
      y: clampPctValue(y + (by - 50) * 0.12),
      derived: true
    })),
    ...lanes.map((y, i) => ({
      team: 'B',
      role: i === 2 ? 'défenseur' : 'milieu',
      x: clampPctValue(defendX + (i === 2 ? -5 : i % 2 ? 5 : 0)),
      y: clampPctValue(y + (by - 50) * 0.08),
      derived: true
    }))
  ];
}

function inferCoachArrows(players, ball, stepIndex, stepCount, contextArrows) {
  if (contextArrows.length) return contextArrows.slice(0, 4);
  const carrier = nearestPlayer(players.filter((p) => p.team === 'A'), ball) || nearestPlayer(players, ball);
  if (!carrier) return [];
  const forward = stepCount > 1 ? 12 + (stepIndex / Math.max(1, stepCount - 1)) * 8 : 14;
  const target = players
    .filter((p) => p.team === carrier.team && p !== carrier && p.x >= carrier.x - 4)
    .sort((a, b) => Math.hypot(a.x - (carrier.x + forward), a.y - carrier.y) - Math.hypot(b.x - (carrier.x + forward), b.y - carrier.y))[0];
  const arrows = [];
  arrows.push({
    type: 'run',
    from_x: carrier.x,
    from_y: carrier.y,
    to_x: clampPctValue(carrier.x + forward),
    to_y: clampPctValue(carrier.y + (carrier.y < 50 ? 8 : -8)),
    label: 'projection'
  });
  if (target) {
    arrows.push({
      type: 'pass',
      from_x: carrier.x,
      from_y: carrier.y,
      to_x: target.x,
      to_y: target.y,
      label: 'relais'
    });
  }
  return arrows;
}

function enrichAnnotations(annotations, zones, players, ball) {
  const out = Array.isArray(annotations) ? annotations.slice(0, 4) : [];
  zones.forEach((z) => out.push({ x: z.x, y: z.y, label: z.label || 'zone clé' }));
  if (ball && out.length < 4) out.push({ x: ball.x, y: ball.y, label: 'zone active' });
  const gap = largestTeamGap(players);
  if (gap && out.length < 4) out.push(gap);
  return out.slice(0, 6);
}

function largestTeamGap(players) {
  const byTeam = groupByTeam(players);
  const a = centroid(byTeam.A || []);
  const b = centroid(byTeam.B || []);
  if (!Number.isFinite(a.x) || !Number.isFinite(b.x)) return null;
  return {
    x: clampPctValue((a.x + b.x) / 2),
    y: clampPctValue((a.y + b.y) / 2),
    label: 'espace entre lignes'
  };
}

function drawTeamBlocks(layer, width, height, players) {
  const byTeam = groupByTeam(players);
  Object.entries(byTeam).forEach(([team, teamPlayers]) => {
    if (teamPlayers.length < 3) return;
    const box = paddedBox(teamPlayers, 5);
    const color = teamColor(team);
    layer.add(new Konva.Rect({
      x: pct(box.minX, width),
      y: pct(box.minY, height),
      width: pct(box.maxX - box.minX, width),
      height: pct(box.maxY - box.minY, height),
      fill: color.soft,
      stroke: color.line,
      strokeWidth: 1.5,
      dash: [6, 5],
      cornerRadius: 8
    }));
    addSmallLabel(layer, pct(box.minX, width) + 6, pct(box.minY, height) + 6, `Bloc ${team}`, color.main);
  });
}

function drawLaneGuides(layer, width, height) {
  const laneXs = [20, 40, 60, 80];
  laneXs.forEach((x) => {
    layer.add(new Konva.Line({
      points: [pct(x, width), 0, pct(x, width), height],
      stroke: '#ffffff',
      strokeWidth: 1,
      dash: [5, 8],
      opacity: 0.18
    }));
  });
}

function drawActiveZone(layer, width, height, step, players) {
  const ball = normalizeBall(step.ball_position);
  const center = ball || centroid(players);
  if (!Number.isFinite(center.x) || !Number.isFinite(center.y)) return;
  layer.add(new Konva.Ellipse({
    x: pct(center.x, width),
    y: pct(center.y, height),
    radiusX: width * 0.14,
    radiusY: height * 0.22,
    fill: '#f2c94c',
    opacity: 0.13
  }));
}

function drawTeamLines(layer, width, height, players) {
  inferStoryboardTeamLines(players).forEach((line) => {
    const color = teamColor(line.team);
    const points = line.players
      .slice()
      .sort((a, b) => a.y - b.y)
      .flatMap((p) => [pct(p.x, width), pct(p.y, height)]);
    if (points.length < 4) return;
    layer.add(new Konva.Line({
      points,
      stroke: color.line,
      strokeWidth: 3,
      lineCap: 'round',
      lineJoin: 'round',
      opacity: 0.96
    }));
    const c = centroid(line.players);
    addSmallLabel(layer, pct(c.x, width) + 6, pct(c.y, height) - 18, line.label, color.main);
  });
}

function drawBlockEvolution(layer, width, height, players, previousPlayers) {
  if (!previousPlayers.length) return;
  ['A', 'B'].forEach((team) => {
    const current = players.filter((p) => p.team === team);
    const previous = previousPlayers.filter((p) => p.team === team);
    if (current.length < 2 || previous.length < 2) return;
    const a = centroid(previous);
    const b = centroid(current);
    const dist = Math.hypot(b.x - a.x, b.y - a.y);
    if (dist < 4) return;
    const color = teamColor(team);
    layer.add(new Konva.Arrow({
      points: [pct(a.x, width), pct(a.y, height), pct(b.x, width), pct(b.y, height)],
      pointerLength: 8,
      pointerWidth: 6,
      stroke: color.main,
      fill: color.main,
      strokeWidth: 2,
      dash: [4, 5],
      opacity: 0.58
    }));
  });
}

function drawSupportNetwork(layer, width, height, players, ball) {
  const carrier = nearestPlayer(players, normalizeBall(ball));
  const byTeam = groupByTeam(players);
  Object.entries(byTeam).forEach(([team, list]) => {
    const color = teamColor(team);
    const edges = supportEdges(list, carrier?.team === team ? carrier : null);
    edges.forEach(([a, b], index) => {
      layer.add(new Konva.Line({
        points: [pct(a.x, width), pct(a.y, height), pct(b.x, width), pct(b.y, height)],
        stroke: color.line,
        strokeWidth: index < 3 ? 2 : 1.2,
        opacity: index < 3 ? 0.58 : 0.26,
        dash: index < 3 ? [] : [4, 5]
      }));
    });
  });
}

function drawBallTrail(layer, width, height, previousBall, currentBall) {
  const a = normalizeBall(previousBall);
  const b = normalizeBall(currentBall);
  if (!a || !b) return;
  if (Math.hypot(b.x - a.x, b.y - a.y) < 3) return;
  layer.add(new Konva.Arrow({
    points: [pct(a.x, width), pct(a.y, height), pct(b.x, width), pct(b.y, height)],
    pointerLength: 9,
    pointerWidth: 7,
    stroke: '#ffffff',
    fill: '#ffffff',
    strokeWidth: 2,
    dash: [6, 5],
    opacity: 0.75
  }));
}

function supportEdges(players, carrier) {
  const anchors = carrier ? [carrier, ...players.filter((p) => p !== carrier)] : players;
  const edges = [];
  const seen = new Set();
  anchors.forEach((p) => {
    players
      .filter((q) => q !== p)
      .map((q) => ({ q, d: Math.hypot(q.x - p.x, q.y - p.y) }))
      .filter((x) => x.d <= 34)
      .sort((a, b) => a.d - b.d)
      .slice(0, carrier && p === carrier ? 3 : 1)
      .forEach(({ q }) => {
        const key = `${Math.min(players.indexOf(p), players.indexOf(q))}-${Math.max(players.indexOf(p), players.indexOf(q))}`;
        if (seen.has(key)) return;
        seen.add(key);
        edges.push([p, q]);
      });
  });
  return edges.slice(0, 10);
}

function inferLinesForTeam(team, players) {
  if (players.length < 2) return [];
  const buckets = [
    { key: 'defense', label: 'ligne défensive', re: /def|déf|back|gard|goal/i, players: [] },
    { key: 'midfield', label: 'ligne médiane', re: /mil|mid|relay|relayeur|sentinelle/i, players: [] },
    { key: 'attack', label: 'ligne offensive', re: /att|avant|wing|ail|striker|buteur/i, players: [] }
  ];
  players.forEach((p) => {
    const bucket = buckets.find((b) => b.re.test(p.role));
    if (bucket) bucket.players.push(p);
  });

  const assigned = new Set(buckets.flatMap((b) => b.players));
  const unknown = players.filter((p) => !assigned.has(p)).sort((a, b) => a.x - b.x);
  if (unknown.length >= 4 && buckets.every((b) => b.players.length === 0)) {
    const split = [unknown.slice(0, Math.ceil(unknown.length / 3)), unknown.slice(Math.ceil(unknown.length / 3), Math.ceil((unknown.length * 2) / 3)), unknown.slice(Math.ceil((unknown.length * 2) / 3))];
    split.forEach((items, i) => { buckets[i].players = items; });
  }

  return buckets
    .filter((b) => b.players.length >= 2)
    .map((b) => ({ team, key: b.key, label: b.label, players: b.players }));
}

function groupByTeam(players) {
  return players.reduce((acc, p) => {
    (acc[p.team] ||= []).push(p);
    return acc;
  }, {});
}

function paddedBox(players, pad) {
  const xs = players.map((p) => p.x);
  const ys = players.map((p) => p.y);
  return {
    minX: clampPctValue(Math.min(...xs) - pad),
    maxX: clampPctValue(Math.max(...xs) + pad),
    minY: clampPctValue(Math.min(...ys) - pad),
    maxY: clampPctValue(Math.max(...ys) + pad)
  };
}

function centroid(players) {
  if (!Array.isArray(players) || players.length === 0) return { x: NaN, y: NaN };
  const n = players.length;
  return {
    x: players.reduce((sum, p) => sum + p.x, 0) / n,
    y: players.reduce((sum, p) => sum + p.y, 0) / n
  };
}

function nearestPlayer(players, ball) {
  if (!ball || !players.length) return null;
  return players
    .map((p) => ({ p, d: Math.hypot(p.x - ball.x, p.y - ball.y) }))
    .sort((a, b) => a.d - b.d)[0]?.p || null;
}

function inferBallFromArrows(arrows) {
  const list = normalizeViewArrows(arrows);
  if (!list.length) return null;
  return { x: clampPctValue(list[0].from_x), y: clampPctValue(list[0].from_y) };
}

function inferBallFromPlayers(players) {
  const list = normalizeViewPlayers(players);
  if (!list.length) return null;
  return centroid(list);
}

function roleFamily(role) {
  const label = roleLabel(role);
  return label || 'X';
}

function teamColor(team) {
  return TEAM_COLORS[team] || TEAM_COLORS.A;
}

function addSmallLabel(layer, x, y, text, color) {
  layer.add(new Konva.Label({ x, y })
    .add(new Konva.Tag({ fill: '#ffffff', opacity: 0.9, cornerRadius: 4 }))
    .add(new Konva.Text({ text, fontSize: 10, fill: color, fontStyle: 'bold', padding: 4 })));
}

function drawPitch(layer, w, h) {
  const c = '#ffffff';
  layer.add(new Konva.Rect({ x: 0, y: 0, width: w, height: h, fill: '#0d6b3b' }));
  for (let i = 0; i < 10; i++) {
    layer.add(new Konva.Rect({
      x: (w / 10) * i,
      y: 0,
      width: w / 10,
      height: h,
      fill: i % 2 ? '#0f7541' : '#0d6b3b',
      opacity: 0.55
    }));
  }
  layer.add(new Konva.Rect({ x: 0, y: 0, width: w, height: h, stroke: c, strokeWidth: 2 }));
  layer.add(new Konva.Line({ points: [w / 2, 0, w / 2, h], stroke: c, strokeWidth: 2 }));
  layer.add(new Konva.Circle({ x: w / 2, y: h / 2, radius: h * 0.16, stroke: c, strokeWidth: 2 }));
  layer.add(new Konva.Circle({ x: w / 2, y: h / 2, radius: 2, fill: c }));
  const paW = w * 0.16;
  const paH = h * 0.62;
  const sixW = w * 0.055;
  const sixH = h * 0.32;
  layer.add(new Konva.Rect({ x: 0, y: (h - paH) / 2, width: paW, height: paH, stroke: c, strokeWidth: 2 }));
  layer.add(new Konva.Rect({ x: w - paW, y: (h - paH) / 2, width: paW, height: paH, stroke: c, strokeWidth: 2 }));
  layer.add(new Konva.Rect({ x: 0, y: (h - sixH) / 2, width: sixW, height: sixH, stroke: c, strokeWidth: 2 }));
  layer.add(new Konva.Rect({ x: w - sixW, y: (h - sixH) / 2, width: sixW, height: sixH, stroke: c, strokeWidth: 2 }));
}

function pct(value, max) {
  return (clampPctValue(Number(value) || 0) / 100) * max;
}

function clampPctValue(value) {
  return Math.max(0, Math.min(100, value));
}

function roleLabel(role = '') {
  const r = String(role).toLowerCase();
  if (/gard|goal/.test(r)) return 'G';
  if (/def|déf|back/.test(r)) return 'D';
  if (/mil|mid/.test(r)) return 'M';
  if (/att|avant|wing|ail|striker/.test(r)) return 'A';
  return '';
}
